/**
 * Contrato do plano de saúde e da agenda de médicos da família.
 *
 * Papel: aqui ficam os schemas dos dois cadastros de Saúde —
 * `novoPlanoSaudeSchema`, `novaFotoDaCarteirinhaSchema` e
 * `novoContatoMedicoSchema` na entrada, `planoSaudeSchema` e
 * `contatoMedicoSchema` na saída — e `nomeDoArquivoDaCarteirinha`, o nome com
 * que a foto da carteirinha é guardada e baixada.
 *
 * Como usar: uma pessoa tem uma linha por apólice, e não uma por operadora —
 * medicina e odontologia da mesma operadora são dois contratos e duas linhas. O
 * banco recusa a mesma apólice repetida para a mesma pessoa, com 409.
 *
 * `carteirinha` não passa por normalização nenhuma. Os espaços de
 * `12345678 00 01 2` são o formato da operadora, e não pontuação nossa a
 * desfazer — ao contrário do telefone e do CPF, aqui não há forma canônica a
 * extrair, e inventá-la produziria um número que não existe em lugar nenhum.
 */
import { z } from 'zod';

import {
  dataOpcionalSchema,
  emailOpcionalSchema,
  idSchema,
  telefoneOpcionalSchema,
  textoObrigatorio,
  textoOpcional,
} from './comum';
import { diaNoEscritorio } from './financas-folha';
import { cnsOpcionalSchema } from './formatos';

/**
 * O que o formulário envia para criar ou alterar um plano.
 *
 * `operadora` e `plano` são texto, e não escolha entre valores fixos: eles
 * mudam por contrato com a operadora, não por decisão de quem escreve o código,
 * e um enum faria a renovação do plano depender de uma migration.
 *
 * `ingresso` e `validade` admitem ausência ainda que os registros de origem os
 * tragam preenchidos: plano sem prazo existe, e `validade` nula é o que o aviso
 * de vencimento sabe pular. A alternativa seria inventar uma data distante para
 * significar "não vence", que é o tipo de valor que ninguém interpreta dois
 * anos depois.
 *
 * A validade anterior ao ingresso é recusada: é erro de digitação, e passa
 * despercebida por anos num cadastro que ninguém relê.
 */
export const novoPlanoSaudeSchema = z
  .object({
    pessoaId: idSchema,
    operadora: textoObrigatorio('Operadora', 120),
    plano: textoObrigatorio('Plano', 200),
    carteirinha: textoObrigatorio('Carteirinha', 60),
    cns: cnsOpcionalSchema,
    ingresso: dataOpcionalSchema,
    validade: dataOpcionalSchema,
    observacao: textoOpcional(2000),
  })
  .refine(
    (plano) =>
      plano.ingresso === null || plano.validade === null || plano.validade >= plano.ingresso,
    { message: 'A validade não pode ser anterior ao ingresso.', path: ['validade'] },
  );

export type NovoPlanoSaude = z.infer<typeof novoPlanoSaudeSchema>;

/** A pessoa titular do plano, como a listagem a traz aninhada. */
export const pessoaDoPlanoSchema = z.object({
  id: idSchema,
  nomeCompleto: z.string(),
  apelido: z.string(),
});

/**
 * A foto da carteirinha de um plano, como a API a descreve.
 *
 * `nome` é o nome do arquivo, no formato de `nomeDoArquivoDaCarteirinha`, e
 * nunca um endereço: a imagem só é lida por `GET /api/planos-saude/{id}/foto`,
 * que exige o token, e nenhum endereço direto do armazenamento chega ao
 * navegador. `atualizadaEm` muda a cada envio, e por isso serve também para
 * invalidar a imagem que o navegador guardou em cache.
 */
export const fotoDaCarteirinhaSchema = z.object({
  nome: z.string(),
  atualizadaEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type FotoDaCarteirinha = z.infer<typeof fotoDaCarteirinhaSchema>;

/**
 * Um plano como a API o devolve.
 *
 * A pessoa vem aninhada pelo mesmo motivo que em Documentos: a tabela mostra o
 * nome em toda linha, e sem ela a tela faria uma segunda consulta só para
 * traduzir quatro identificadores.
 *
 * `foto` é `null` no plano sem carteirinha guardada. As listagens a trazem sem
 * ler a imagem, de modo que a tabela sabe quais linhas têm foto sem baixar
 * nenhuma.
 */
export const planoSaudeSchema = z.object({
  id: idSchema,
  pessoa: pessoaDoPlanoSchema,
  operadora: z.string(),
  plano: z.string(),
  carteirinha: z.string(),
  cns: z.string().nullable(),
  ingresso: z.string().nullable(),
  validade: z.string().nullable(),
  observacao: z.string().nullable(),
  foto: fotoDaCarteirinhaSchema.nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type PlanoSaude = z.infer<typeof planoSaudeSchema>;

/** Os tipos de imagem aceitos para a carteirinha, com a extensão de cada um. */
export const EXTENSAO_DA_FOTO = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
} as const;

export type TipoDaFotoDaCarteirinha = keyof typeof EXTENSAO_DA_FOTO;
export type ExtensaoDaFotoDaCarteirinha = (typeof EXTENSAO_DA_FOTO)[TipoDaFotoDaCarteirinha];

/** O limite da imagem em caracteres de data URL — uns 3 MB de imagem. */
const LIMITE_DA_FOTO = 4_000_000;

/** A data URL de uma imagem aceita: o tipo declarado e o conteúdo em base64. */
const DATA_URL_DA_FOTO = /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * O que a tela envia para guardar ou trocar a foto da carteirinha.
 *
 * `foto` é a imagem inteira como data URL, `data:image/jpeg;base64,…`, e só
 * JPEG, PNG e WEBP passam. O limite de 4.000.000 de caracteres é conferido
 * antes do formato, e a tela deverá validar o arquivo escolhido com este mesmo
 * schema antes de enviá-lo: sem isso, uma foto grande demais só é recusada
 * depois de atravessar a rede inteira.
 *
 * O schema confere apenas o que a data URL declara. Que os bytes sejam de fato
 * a imagem do tipo declarado é conferido pela API ao decodificá-los, e um
 * arquivo renomeado de `.pdf` para `.jpg` passa aqui e volta com 400 de lá.
 */
export const novaFotoDaCarteirinhaSchema = z.object({
  foto: z
    .string({ message: 'Escolha uma imagem.' })
    .min(1, { message: 'Escolha uma imagem.' })
    .max(LIMITE_DA_FOTO, { message: 'Imagem grande demais. O limite é de uns 3 MB.' })
    .regex(DATA_URL_DA_FOTO, { message: 'A foto precisa ser uma imagem JPEG, PNG ou WEBP.' }),
});

export type NovaFotoDaCarteirinha = z.infer<typeof novaFotoDaCarteirinhaSchema>;

/** As partículas que não contam como sobrenome: o "de" de "Silva de Souza". */
const PARTICULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/**
 * Tira acento e caixa de um texto, para comparar ou para compor o nome.
 *
 * @param texto - Qualquer texto.
 * @returns O texto em minúsculas, sem os diacríticos.
 */
function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Uma palavra do nome como ela entra no nome do arquivo.
 *
 * @param palavra - Uma palavra do nome completo.
 * @returns A palavra em maiúsculas e sem acento, só com letras, dígitos e hífen
 *   — o apóstrofo de `D'Ávila` cai, e sobra `DAVILA`.
 */
function palavraDoArquivo(palavra: string): string {
  return semAcento(palavra)
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '');
}

/**
 * O tipo da carteirinha, lido do nome do plano.
 *
 * @param plano - O texto de `plano`, como `Odonto Prata` ou `Nacional Flex`.
 * @returns `ODONTOLOGIA` quando o texto contém "odonto", em qualquer caixa e
 *   com ou sem acento, e `MEDICINA` em todos os outros casos.
 */
export function tipoDaCarteirinha(plano: string): 'ODONTOLOGIA' | 'MEDICINA' {
  return semAcento(plano).includes('odonto') ? 'ODONTOLOGIA' : 'MEDICINA';
}

/**
 * O nome com que a foto da carteirinha é guardada e baixada.
 *
 * Quem chama é a API, ao receber a foto, e a tela, para mostrar o nome antes do
 * envio. O formato é `NOME_SOBRENOME_TIPO_AAAA-MM-DD.ext`: `NOME` é a primeira
 * palavra de `nomeCompleto` e `SOBRENOME` a seguinte que não for partícula (de,
 * da, do, das, dos, e). É o primeiro sobrenome, e não o último, que entra: duas
 * pessoas com o mesmo primeiro nome costumam dividir o último sobrenome da
 * família, e só o primeiro as distingue. Nome de uma palavra só fica sem
 * `SOBRENOME`.
 *
 * O dia é o de `instante` no fuso do escritório; com `toISOString`, a foto
 * enviada às 22 h sairia com a data do dia seguinte.
 *
 * O resultado só tem `A-Z`, `0-9`, `_`, `-` e `.`, e por isso cabe sem escape
 * num caminho de armazenamento e num `Content-Disposition`.
 *
 * @param entrada - `nomeCompleto` da pessoa do plano, o texto de `plano`, o
 *   instante do envio e a extensão do tipo da imagem, de `EXTENSAO_DA_FOTO`.
 * @returns O nome do arquivo, como `NOME_SOBRENOME_MEDICINA_2026-10-15.jpg`.
 */
export function nomeDoArquivoDaCarteirinha(entrada: {
  nomeCompleto: string;
  plano: string;
  instante: Date;
  extensao: ExtensaoDaFotoDaCarteirinha;
}): string {
  const palavras = entrada.nomeCompleto.split(/\s+/).filter((palavra) => palavra !== '');
  const partes: string[] = [];

  const [primeira, ...demais] = palavras;
  if (primeira !== undefined) partes.push(palavraDoArquivo(primeira));

  const sobrenome = demais.find((palavra) => !PARTICULAS.has(semAcento(palavra)));
  if (sobrenome !== undefined) partes.push(palavraDoArquivo(sobrenome));

  const pessoa = partes.filter((parte) => parte !== '');
  if (pessoa.length === 0) pessoa.push('SEM-NOME');

  const dia = diaNoEscritorio(entrada.instante);
  return `${[...pessoa, tipoDaCarteirinha(entrada.plano), dia].join('_')}.${entrada.extensao}`;
}

/**
 * O que o formulário envia para criar ou alterar um médico da agenda.
 *
 * `paciente` é texto livre, e não uma referência a `pessoas_familia`: um médico
 * atende o casal, os dois filhos ou a família inteira, e nenhuma dessas
 * respostas cabe numa chave estrangeira.
 *
 * Só `nome` é obrigatório, como na agenda do Mailing — um médico de quem se tem
 * apenas o nome e o telefone precisa poder ser cadastrado sem que se invente
 * especialidade nem endereço.
 */
export const novoContatoMedicoSchema = z.object({
  nome: textoObrigatorio('Nome'),
  especialidade: textoOpcional(120),
  paciente: textoOpcional(200),
  telefone: telefoneOpcionalSchema,
  email: emailOpcionalSchema,
  endereco: textoOpcional(300),
  observacao: textoOpcional(2000),
});

export type NovoContatoMedico = z.infer<typeof novoContatoMedicoSchema>;

/** Um médico da agenda como a API o devolve. */
export const contatoMedicoSchema = z.object({
  id: idSchema,
  nome: z.string(),
  especialidade: z.string().nullable(),
  paciente: z.string().nullable(),
  telefone: z.string().nullable(),
  email: z.string().nullable(),
  endereco: z.string().nullable(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type ContatoMedico = z.infer<typeof contatoMedicoSchema>;
